import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, NgZone, ElementRef, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AutoUnsubscribe } from '@app/shared/decorators/auto-unsubscribe.decorator';
import { SharedVaultService } from '@app/shared/services/vault/shared-vault.service';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';

@AutoUnsubscribe()
@Component({
  selector: 'show-suggestion-form',
  templateUrl: './show-suggestion-form.component.html',
  styleUrls: ['./show-suggestion-form.component.scss'],
})
export class ShowSuggestionFormComponent implements OnInit, OnDestroy {
  @ViewChild('editableInput') editableInputRef!: ElementRef;
  private hasFocused = false;
  suggested_documents: any[] = [];
  document_name = 'Untitled Document';
  subscription = new Subscription();
  selectedDocuments: any = [];
  allChecked = false;
  lastAddedId: number | null = null;
  isLoading = true;
  constructor(
    @Inject(MAT_DIALOG_DATA) public data,
    public dialogRef: MatDialogRef<ShowSuggestionFormComponent>,
    private sharedVaultService: SharedVaultService,
    private cdr: ChangeDetectorRef,
    private zone: NgZone,
    private toastr: ToastrService
  ) {}
  ngOnInit(): void {
    this.document_name = this.data.document_name;
    this.getVaultUserFormDocumentSuggestion();
  }

  getVaultUserFormDocumentSuggestion() {
    this.subscription.add(
      this.sharedVaultService.getVaultUserFormDocumentSuggestion(this.data.user_id, this.data.form_id).subscribe((res) => {
        this.isLoading = false;
        let docs = res.map((doc: any) => ({
          ...doc,
          isEditing: this.lastAddedId ? doc.id === this.lastAddedId : false,
          isSelected: false,
        }));
        if (this.lastAddedId) {
          const newItem = docs.find((doc) => doc.id === this.lastAddedId);
          if (newItem) {
            // Remove the new item from its current position
            docs = docs.filter((doc) => doc.id !== this.lastAddedId);
            // Add it to the top
            docs.unshift(newItem);
          }
          this.lastAddedId = null;
        }
        this.suggested_documents = docs;
        this.cdr.detectChanges();
      })
    );
  }

  addNewDocument(event: any) {
    const payload = {
      vault_user_form_id: this.data.form_id,
      suggestion: 'Untitled Document',
    };
    this.subscription.add(
      this.sharedVaultService.createVaultUserFormDocumentSuggestion(payload).subscribe((res) => {
        this.lastAddedId = res.id;
        this.getVaultUserFormDocumentSuggestion();
        this.clearSelection();
      })
    );
  }
  toggleSelection(event: any, suggestion: any, index: number): void {
    const isChecked = event?.source?.checked;

    // Update the selected state of the clicked item
    this.suggested_documents[index].isSelected = isChecked;

    // Update the selectedDocuments list
    this.selectedDocuments = this.suggested_documents.filter((doc) => doc.isSelected);

    this.allChecked = this.suggested_documents.every((doc: any) => doc.isSelected);
  }

  allToggleSelection(suggested_documents: any) {
    const shouldSelectAll = !suggested_documents.every((doc: any) => doc.isSelected);

    suggested_documents.forEach((doc: any) => (doc.isSelected = shouldSelectAll));

    this.selectedDocuments = suggested_documents.filter((doc: any) => doc.isSelected);

    this.allChecked = shouldSelectAll;
  }
  ngAfterViewChecked(): void {
    if (this.editableInputRef && !this.hasFocused) {
      const input: HTMLInputElement = this.editableInputRef.nativeElement;
      input.focus();
      input.select(); // Select all text
      this.hasFocused = true;
    }
  }

  setEditMode(event: MouseEvent, suggestion: any) {
    suggestion.isEditing = true;
    const inputElement = event.target as HTMLInputElement;
    inputElement.focus();
    inputElement.select();
  }
  saveEdit(suggestion: any) {
    if (!suggestion.suggestion || suggestion.suggestion.trim() === '') {
      suggestion.isEditing = true;
      return;
    }
    suggestion.isEditing = false;
    const payload = {
      id: suggestion.id,
      vault_user_form_id: this.data.form_id,
      suggestion: suggestion.suggestion.trim(),
    };
    this.subscription.add(
      this.sharedVaultService.updateVaultUserFormDocumentSuggestion(payload).subscribe((res) => {
        this.getVaultUserFormDocumentSuggestion();
      })
    );
  }
  clearSelection() {
    this.selectedDocuments = [];
    this.allChecked = false;
    this.suggested_documents.forEach((doc: any) => (doc.isSelected = false));
  }
  deleteSuggestion(index: number) {
    let suggestionsToDelete: string[] = [];
    if (this.selectedDocuments.length > 0) {
      suggestionsToDelete = this.selectedDocuments.map((doc: any) => doc.id);
    } else if (index !== undefined) {
      const suggestion = this.suggested_documents[index]?.id;
      if (suggestion) {
        suggestionsToDelete = [suggestion];
      }
    }
    if (!suggestionsToDelete.length) return;
    const payload = {
      formid: this.data.form_id,
      suggestionIds: suggestionsToDelete,
    };
    this.subscription.add(
      this.sharedVaultService.deleteVaultUserFormDocumentSuggestion(payload).subscribe((res) => {
        this.selectedDocuments = [];
        this.allChecked = false;
        this.suggested_documents.forEach((doc: any) => (doc.isSelected = false));
        this.getVaultUserFormDocumentSuggestion();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
  closeDialog() {
    this.dialogRef.close();
  }
  trackById(index: number, item: any) {
    return item.id;
  }
}
